import React, { useState, useEffect } from 'react';
import { 
  Snackbar, 
  Alert, 
  Button, 
  Box, 
  Typography,
  IconButton 
} from '@mui/material';
import { 
  SystemUpdate, 
  Refresh, 
  Close, 
  CloudDownload,
  Wifi,
  WifiOff 
} from '@mui/icons-material';
import { useHapticFeedback } from '../utils/haptics';

/**
 * PWA Update and Offline Status Component
 * Handles PWA updates, offline status, and installation prompts
 */
const PWAHandler = () => {
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showOfflineAlert, setShowOfflineAlert] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [registration, setRegistration] = useState(null);
  
  const haptic = useHapticFeedback();

  useEffect(() => {
    // Listen for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        setRegistration(reg);
        
        // Listen for updates
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setShowUpdateAlert(true);
                haptic.importantAction();
              }
            });
          }
        });
      });
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Don't show if already installed
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowInstallPrompt(true);
      }
    };

    // Listen for online/offline status
    const handleOnline = () => {
      setIsOnline(true);
      setShowOfflineAlert(false);
      haptic.success();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineAlert(true);
      haptic.error();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check if running as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('Running as PWA');
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [haptic]);

  // Reset zoom to 1.0 when rotating back to portrait
  useEffect(() => {
    const portraitQuery = window.matchMedia('(orientation: portrait)');

    const resetZoom = () => {
      // Only act when switching TO portrait
      if (!portraitQuery.matches) return;

      // Only reset if actually zoomed in
      const currentScale = window.visualViewport?.scale ?? 1;
      if (currentScale <= 1.05) return;

      const viewport = document.querySelector('meta[name="viewport"]');
      if (!viewport) return;

      const original = viewport.getAttribute('content');
      // Temporarily force scale=1, then restore to allow future zoom
      viewport.setAttribute('content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
      requestAnimationFrame(() => {
        setTimeout(() => {
          viewport.setAttribute('content', original);
        }, 100);
      });
    };

    // Modern API: MediaQueryList change event
    portraitQuery.addEventListener('change', resetZoom);

    return () => {
      portraitQuery.removeEventListener('change', resetZoom);
    };
  }, []);

  const handleUpdate = async () => {
    if (registration && registration.waiting) {
      haptic.importantAction();
      
      // Tell the waiting service worker to skip waiting
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Reload the page
      window.location.reload();
    }
    setShowUpdateAlert(false);
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      haptic.importantAction();
      deferredPrompt.prompt();
      
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('PWA installed successfully');
        haptic.success();
      } else {
        console.log('PWA installation declined');
      }
      
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  };

  const dismissInstallPrompt = () => {
    haptic.buttonPress();
    setShowInstallPrompt(false);
    setDeferredPrompt(null);
  };

  const dismissOfflineAlert = () => {
    haptic.buttonPress();
    setShowOfflineAlert(false);
  };

  return (
    <>
      {/* Update Available Alert */}
      <Snackbar
        open={showUpdateAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert
          severity="info"
          icon={<SystemUpdate />}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                color="inherit"
                size="small"
                startIcon={<Refresh />}
                onClick={handleUpdate}
                sx={{ 
                  minHeight: '44px',
                  fontWeight: 'bold'
                }}
              >
                Aktualisieren
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => {
                  haptic.buttonPress();
                  setShowUpdateAlert(false);
                }}
                sx={{ minWidth: '44px', minHeight: '44px' }}
              >
                <Close />
              </IconButton>
            </Box>
          }
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': { 
              display: 'flex', 
              alignItems: 'center',
              flex: 1
            }
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Neue Version verfügbar
            </Typography>
            <Typography variant="body2">
              Aktualisieren Sie für die neuesten Features
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      {/* Install App Prompt */}
      <Snackbar
        open={showInstallPrompt}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={10000}
        onClose={dismissInstallPrompt}
      >
        <Alert
          severity="success"
          icon={<CloudDownload />}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                color="inherit"
                size="small"
                onClick={handleInstall}
                sx={{ 
                  minHeight: '44px',
                  fontWeight: 'bold'
                }}
              >
                Installieren
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={dismissInstallPrompt}
                sx={{ minWidth: '44px', minHeight: '44px' }}
              >
                <Close />
              </IconButton>
            </Box>
          }
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': { 
              display: 'flex', 
              alignItems: 'center',
              flex: 1
            }
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Object-Link als App installieren
            </Typography>
            <Typography variant="body2">
              Für schnelleren Zugriff und bessere Performance
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      {/* Offline Status Alert */}
      <Snackbar
        open={showOfflineAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={5000}
        onClose={dismissOfflineAlert}
      >
        <Alert
          severity="warning"
          icon={<WifiOff />}
          action={
            <IconButton
              size="small"
              color="inherit"
              onClick={dismissOfflineAlert}
              sx={{ minWidth: '44px', minHeight: '44px' }}
            >
              <Close />
            </IconButton>
          }
          sx={{ width: '100%' }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Offline-Modus
            </Typography>
            <Typography variant="body2">
              Einige Funktionen sind eingeschränkt
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      {/* Online Status Indicator (subtle) */}
      {!isOnline && (
        <Box
          sx={{
            position: 'fixed',
            top: 8,
            right: 8,
            zIndex: 9999,
            backgroundColor: 'warning.main',
            color: 'warning.contrastText',
            borderRadius: 1,
            p: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.75rem'
          }}
        >
          <WifiOff fontSize="small" />
          <Typography variant="caption">Offline</Typography>
        </Box>
      )}
    </>
  );
};

export default PWAHandler;